<template>
    <span class="kpi-help" :style="{'--help-accent': color}">
        <button type="button"
            class="kpi-help-trigger"
            :aria-describedby="id"
            :aria-label="`Como ${label} é comparado`">?</button>

        <span class="kpi-help-popover" role="tooltip" :id="id">
            <span class="kpi-help-arrow"></span>

            <span class="kpi-help-eyebrow">{{label}}</span>
            <span class="kpi-help-title">Como esta comparação é feita</span>
            <span class="kpi-help-text">
                Comparamos o período filtrado com os <b>{{days}} {{days === 1 ? 'dia' : 'dias'}}</b> imediatamente antes dele.
            </span>

            <span class="kpi-help-timeline" v-if="previousRange && currentRange">
                <span class="kpi-help-segment is-previous">
                    <span class="kpi-help-bar"></span>
                    <span class="kpi-help-segment-label">Anterior</span>
                    <span class="kpi-help-segment-dates">{{formatDate(previousRange.start)}} a {{formatDate(previousRange.end)}}</span>
                </span>

                <span class="kpi-help-segment is-current">
                    <span class="kpi-help-bar">
                        <span class="kpi-help-today" v-if="includesToday" title="Hoje"></span>
                    </span>
                    <span class="kpi-help-segment-label">Filtrado</span>
                    <span class="kpi-help-segment-dates">{{formatDate(currentRange.start)}} a {{formatDate(currentRange.end)}}</span>
                </span>
            </span>

            <span class="kpi-help-compare">
                <span class="kpi-help-compare-item">
                    <span class="kpi-help-compare-label">Anterior</span>
                    <span class="kpi-help-compare-value is-muted">{{formatValue(kpi.previous)}}</span>
                </span>

                <span class="kpi-help-compare-arrow" aria-hidden="true">→</span>

                <span class="kpi-help-compare-item">
                    <span class="kpi-help-compare-label">Filtrado</span>
                    <span class="kpi-help-compare-value">{{formatValue(kpi.value)}}</span>
                </span>

                <span class="kpi-help-compare-variation" :class="variationClass" v-if="kpi.variation !== null">
                    {{kpi.variation > 0 ? '+' : ''}}{{formatVariation(kpi.variation)}}
                </span>
            </span>

            <span class="kpi-help-formula">
                <span class="kpi-help-formula-label">Cálculo</span>
                {{formula}}
                <template v-if="kpi.variation === null"> Sem valor no período anterior, não há base para a variação.</template>
            </span>

            <span class="kpi-help-note" v-if="includesToday">
                <span class="kpi-help-note-dot"></span>
                O período filtrado inclui hoje, que ainda está em andamento: a variação tende a subir até o fim do dia.
            </span>
        </span>
    </span>
</template>

<script>
import { format, isToday } from 'date-fns'
import { periodDays } from '@/dashboard/normalizers/DashboardKpiNormalizer'

export default {
    name: 'KpiPeriodHelpCmp',
    props: {
        id: {
            type: String,
            required: true,
        },
        label: {
            type: String,
            required: true,
        },
        formula: {
            type: String,
            default: '',
        },
        color: {
            type: String,
            default: '#3b82f6', // theme-ignore: cor de destaque do KPI (dado), vem do pai
        },
        isMoney: {
            type: Boolean,
            default: false,
        },
        kpi: {
            type: Object,
            required: true,
        },
        currentRange: {
            type: Object,
            default: null,
        },
        previousRange: {
            type: Object,
            default: null,
        },
    },
    computed: {
        days() {
            return this.currentRange ? periodDays(this.currentRange) : 0
        },
        includesToday() {
            return !!this.currentRange && isToday(new Date(this.currentRange.end))
        },
        variationClass() {
            if (!this.kpi.variation) {
                return 'is-neutral'
            }

            return this.kpi.variation > 0 ? 'is-up' : 'is-down'
        },
    },
    methods: {
        formatDate(date) {
            return format(new Date(date), 'dd/MM/yyyy')
        },
        formatValue(value) {
            if (this.isMoney) {
                return Number(value || 0).toLocaleString('pt-BR', {style: 'currency', currency: 'BRL'})
            }

            return Number(value || 0).toLocaleString('pt-BR')
        },
        formatVariation(value) {
            return `${value.toLocaleString('pt-BR', {maximumFractionDigits: 1})}%`
        },
    },
}
</script>

<style lang="scss" scoped>
$ink: var(--surface-tooltip-deep);
$ink-soft: var(--surface-tooltip);
$line: color-mix(in srgb, var(--white) 8%, transparent);
$text: var(--text-on-tooltip-soft);
$muted: var(--text-on-tooltip-muted);

.kpi-help {
    position: relative;
    display: inline-flex;
}

.kpi-help-trigger {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    padding: 0;
    font-size: 12px;
    font-weight: 700;
    line-height: 1;
    color: var(--text-faint);
    background-color: var(--surface-3);
    border: 1px solid transparent;
    border-radius: 50%;
    cursor: help;
    transition: color .15s ease, background-color .15s ease, border-color .15s ease;

    &:focus {
        outline: none;
    }
}

.kpi-help:hover .kpi-help-trigger,
.kpi-help-trigger:focus-visible {
    color: var(--text-on-brand);
    background-color: var(--help-accent);
    border-color: var(--help-accent);
}

.kpi-help-popover {
    position: absolute;
    top: calc(100% + 12px);
    right: -10px;
    z-index: 30;
    display: block;
    width: 344px;
    max-width: calc(100vw - 40px);
    padding: 16px 18px 14px;
    text-align: left;
    white-space: normal;
    color: $text;
    background:
        radial-gradient(120% 80% at 100% 0%, color-mix(in srgb, var(--white) 6%, transparent) 0%, transparent 55%),
        linear-gradient(180deg, $ink-soft 0%, $ink 100%);
    border: 1px solid $line;
    border-radius: 14px;
    box-shadow:
        0 24px 48px -12px color-mix(in srgb, var(--shadow-ink) 45%, transparent),
        0 8px 16px -8px color-mix(in srgb, var(--shadow-ink) 30%, transparent);
    opacity: 0;
    visibility: hidden;
    transform: translateY(-4px) scale(.98);
    transform-origin: calc(100% - 20px) 0;
    pointer-events: none;
    // Ao sair, espera um pouco: dá tempo de levar o mouse do botão até o popover.
    transition: opacity .16s ease .12s, transform .16s ease .12s, visibility 0s linear .28s;

    // Ponte invisível entre o botão e o popover, para o hover não cair no vão.
    &::before {
        content: '';
        position: absolute;
        top: -14px;
        left: 0;
        right: 0;
        height: 14px;
    }
}

.kpi-help:hover .kpi-help-popover,
.kpi-help:focus-within .kpi-help-popover {
    opacity: 1;
    visibility: visible;
    transform: none;
    pointer-events: auto;
    transition: opacity .18s ease, transform .22s cubic-bezier(.2, .8, .2, 1), visibility 0s;
}

.kpi-help-arrow {
    position: absolute;
    top: -6px;
    right: 15px;
    width: 12px;
    height: 12px;
    background-color: $ink-soft;
    border-top: 1px solid $line;
    border-left: 1px solid $line;
    border-radius: 3px 0 0 0;
    transform: rotate(45deg);
}

.kpi-help-eyebrow,
.kpi-help-title,
.kpi-help-text,
.kpi-help-timeline,
.kpi-help-compare,
.kpi-help-formula,
.kpi-help-note {
    display: block;
}

.kpi-help-eyebrow {
    font-size: 10px;
    font-weight: 700;
    letter-spacing: .12em;
    text-transform: uppercase;
    color: var(--help-accent);
}

.kpi-help-title {
    margin-top: 2px;
    font-size: 15px;
    font-weight: 700;
    color: var(--text-on-brand);
}

.kpi-help-text {
    margin-top: 4px;
    font-size: 13px;
    line-height: 1.45;
    color: $muted;

    b {
        color: $text;
        font-weight: 600;
    }
}

.kpi-help-timeline {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 6px;
    margin-top: 14px;
}

.kpi-help-segment {
    display: flex;
    flex-direction: column;
    min-width: 0;
}

.kpi-help-bar {
    position: relative;
    display: block;
    height: 8px;
    margin-bottom: 8px;
    border-radius: 999px;

    .is-previous & {
        background: repeating-linear-gradient(
            -45deg,
            color-mix(in srgb, var(--white) 16%, transparent) 0 4px,
            color-mix(in srgb, var(--white) 6%, transparent) 4px 8px
        );
    }

    .is-current & {
        background-color: var(--help-accent);
        box-shadow: 0 0 12px -2px var(--help-accent);
    }
}

.kpi-help-today {
    position: absolute;
    top: 50%;
    right: 1px;
    width: 6px;
    height: 6px;
    margin-top: -3px;
    background-color: var(--white);
    border-radius: 50%;

    &::after {
        content: '';
        position: absolute;
        top: -4px;
        right: -4px;
        bottom: -4px;
        left: -4px;
        border: 2px solid var(--white);
        border-radius: 50%;
        opacity: 0;
        animation: kpi-help-pulse 1.8s ease-out infinite;
    }
}

.kpi-help-segment-label {
    font-size: 10px;
    font-weight: 700;
    letter-spacing: .1em;
    text-transform: uppercase;
    color: $muted;

    .is-current & {
        color: $text;
    }
}

.kpi-help-segment-dates {
    margin-top: 1px;
    font-size: 12.5px;
    font-weight: 600;
    color: $text;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;

    .is-previous & {
        color: $muted;
    }
}

.kpi-help-compare {
    display: flex;
    align-items: flex-end;
    gap: 10px;
    margin-top: 14px;
    padding: 10px 12px;
    background-color: color-mix(in srgb, var(--white) 4%, transparent);
    border: 1px solid $line;
    border-radius: 10px;
}

.kpi-help-compare-item {
    display: flex;
    flex-direction: column;
    min-width: 0;
}

.kpi-help-compare-label {
    font-size: 10px;
    font-weight: 600;
    letter-spacing: .08em;
    text-transform: uppercase;
    color: $muted;
}

.kpi-help-compare-value {
    font-size: 14px;
    font-weight: 700;
    color: var(--text-on-brand);
    font-variant-numeric: tabular-nums;
    white-space: nowrap;

    &.is-muted {
        color: $muted;
    }
}

.kpi-help-compare-arrow {
    padding-bottom: 1px;
    color: $muted;
}

.kpi-help-compare-variation {
    margin-left: auto;
    padding: 2px 8px;
    font-size: 12px;
    font-weight: 700;
    border-radius: 999px;
    font-variant-numeric: tabular-nums;

    &.is-up {
        color: var(--success-on-dark);
        background-color: color-mix(in srgb, var(--success-solid) 15%, transparent);
    }

    &.is-down {
        color: var(--danger-on-dark);
        background-color: color-mix(in srgb, var(--danger-solid) 15%, transparent);
    }

    &.is-neutral {
        color: $muted;
        background-color: color-mix(in srgb, var(--white) 6%, transparent);
    }
}

.kpi-help-formula {
    margin-top: 12px;
    font-size: 12.5px;
    line-height: 1.45;
    color: $muted;
}

.kpi-help-formula-label {
    margin-right: 4px;
    font-weight: 700;
    color: $text;
}

.kpi-help-note {
    position: relative;
    margin-top: 10px;
    padding: 9px 0 0 14px;
    font-size: 12px;
    line-height: 1.45;
    color: $muted;
    border-top: 1px solid $line;
}

.kpi-help-note-dot {
    position: absolute;
    top: 15px;
    left: 0;
    width: 6px;
    height: 6px;
    background-color: var(--icon-highlight);
    border-radius: 50%;
}

@keyframes kpi-help-pulse {
    0% {
        opacity: .7;
        transform: scale(.6);
    }
    100% {
        opacity: 0;
        transform: scale(1.6);
    }
}

@media (prefers-reduced-motion: reduce) {
    .kpi-help-popover,
    .kpi-help:hover .kpi-help-popover,
    .kpi-help:focus-within .kpi-help-popover {
        transition: none;
        transform: none;
    }

    .kpi-help-today::after {
        animation: none;
    }
}
</style>
